import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { z } from "zod";

const AddMeal = () => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { authFetch } = useAuth();

    const mealSchema = z.object({
        meal: z.string().min(1, t("meals.form.meal.error")),
        mealType: z.string().min(1, t("meals.form.mealType.error")),
        location: z.string().min(1, t("meals.form.location.error")),
        eatenAt: z.string().min(1, t("meals.form.date.error")),
        weightCrudeOrCooked: z.string().default("crude"),
    });

    type MealFormData = z.infer<typeof mealSchema>;

    const form = useForm<MealFormData>({
        resolver: zodResolver(mealSchema),
        defaultValues: {
            meal: "",
            mealType: "",
            location: "",
            eatenAt: new Date().toISOString().slice(0, 16),
            weightCrudeOrCooked: "crude",
        },
    });

    const onSubmit = async (data: MealFormData) => {
        try {
            const response = await authFetch("/api/meals/create", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                throw new Error(t("meals.form.error"));
            }

            const result = await response.json();

            toast({
                title: t("meals.form.success"),
                description: t("meals.form.calculatedCalories", {
                    calories: result.calories,
                }),
            });

            navigate("/dashboard");
        } catch (error) {
            toast({
                variant: "destructive",
                title: t("meals.form.error"),
                description: error instanceof Error ? error.message : t("meals.form.error"),
            });
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Navigation />

            <div className="pt-24 pb-6 px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mx-auto">
                    <h1 className="text-3xl font-bold text-gray-900 mb-8">{t("meals.form.title")}</h1>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                            <FormField
                                control={form.control}
                                name="eatenAt"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("meals.form.date")}</FormLabel>
                                        <FormControl>
                                            <Input type="datetime-local" {...field} />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="mealType"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("meals.form.mealType")}</FormLabel>
                                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder={t("meals.form.selectMealType")} />
                                                </SelectTrigger>
                                            </FormControl>

                                            <SelectContent>
                                                {["breakfast", "morningSnack", "lunch", "afternoonSnack", "dinner"].map((mealType) => (
                                                    <SelectItem key={mealType} value={mealType}>
                                                        {t(`meals.types.${mealType}`)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="location"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("meals.form.location")}</FormLabel>
                                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder={t("meals.locations.placeholder")} />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {["home", "restaurant", "work", "walk"].map((location) => (
                                                    <SelectItem key={location} value={location}>
                                                        {t(`meals.locations.${location}`)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="meal"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("meals.form.meal")}</FormLabel>
                                        <FormControl>
                                            <Textarea {...field} placeholder={t("meals.form.mealPlaceholder")} className="min-h-[200px]" />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="weightCrudeOrCooked"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t("meals.form.weightCrudeOrCooked")}</FormLabel>
                                        <FormControl>
                                            <div className="flex space-x-4">
                                                {" "}
                                                {/* Flex per allineamento orizzontale */}
                                                <label className="flex items-center space-x-2">
                                                    {" "}
                                                    {/* Label per allineare radio e testo */}
                                                    <Input
                                                        type="radio"
                                                        {...field}
                                                        value="crudo"
                                                        checked={field.value === "crude"} // Seleziona "crudo"
                                                        className="peer" // Classe per styling con Tailwind (opzionale)
                                                    />
                                                    <span>{t("meals.form.crude")}</span>
                                                </label>
                                                <label className="flex items-center space-x-2">
                                                    <Input
                                                        type="radio"
                                                        {...field}
                                                        value="cotto"
                                                        checked={field.value === "cooked"} // Seleziona "cotto"
                                                        className="peer"
                                                    />
                                                    <span>{t("meals.form.cooked")}</span>
                                                </label>
                                            </div>
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                            <Button type="submit" className="w-full">
                                {t("meals.form.save")}
                            </Button>
                        </form>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default AddMeal;
